import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { EASE } from '../components/motion.tsx';
import { Button, Callout, Chip, Dialog, Field, PageHead, Panel, Segmented, Skeleton, Switch, useToast } from '../components/ui.tsx';
import { ApiError, get, patch, post, put } from '../lib/api.ts';
import { idr } from '../lib/format.ts';
import { useMe } from '../lib/session.ts';

interface Rule {
  id: string;
  amount: string | null;
  useFormula: boolean;
  formula: string | null;
  active: boolean;
  officeId: string | null;
}
interface Component {
  id: string;
  code: string;
  name: string;
  kind: 'earning' | 'deduction';
  role: string | null;
  cooperative: boolean;
  salaryProrate: boolean;
  excludedFromThr: boolean;
  splitPayrollCut: boolean;
  manual: boolean;
  description: string | null;
  archivedAt: string | null;
  rules: Rule[];
}
interface Reference {
  variables: { name: string; label: string; description: string; unit: string; contexts: string[]; legacyNote?: string }[];
  functions: Record<string, { description: string }>;
}

const SAMPLE: Record<string, number> = {
  basic_salary: 5_000_000,
  total_salary: 6_900_000,
  min_late_in: 37,
  min_early_out: 0,
  min_short_leave: 0,
  days_absent: 1,
  min_overtime: 120,
  total_work_days: 26,
  total_working_days: 26,
  holiday_allowance_received: 6_900_000,
  working_months: 5,
  gross_salary_received: 6_300_000,
  previous_salary_received: 2_750_000,
  current_salary_received: 3_250_000,
  other_bonus_prorate: 0,
  other_cut_prorate: 200_000,
};

export function Components() {
  const me = useMe().data!;
  const canEdit = me.access.componentEdit;
  const [showInactive, setShowInactive] = useState(false);
  const q = useQuery({ queryKey: ['components', showInactive], queryFn: () => get<Component[]>(`/api/payroll/components${showInactive ? '?archived=1' : ''}`) });
  const ref = useQuery({ queryKey: ['formula-ref'], queryFn: () => get<Reference>('/api/payroll/formula/reference'), staleTime: Infinity });
  const [selected, setSelected] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'earning' | 'deduction'>('all');
  const [editing, setEditing] = useState<Component | 'new' | null>(null);
  const list = (q.data ?? []).filter((c) => filter === 'all' || c.kind === filter);
  const current = list.find((c) => c.id === selected) ?? list[0];

  return (
    <div>
      <PageHead
        eyebrow={`Payroll · ${q.data?.length ?? '…'} components`}
        title={
          <>
            The rules <em>behind every rupiah.</em>
          </>
        }
        lede={
          <>
            Formulas use a small, safe language: the same <span className="code-inline">_basic_salary_</span> tokens as before, but it can only do arithmetic. The old system ran these through PHP <span className="code-inline">eval()</span>.
          </>
        }
        aside={
          canEdit ? (
            <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
              New component
            </Button>
          ) : null
        }
      />
      <div className="grid-12">
        <div className="col-5">
          <div className="row row--between row--wrap" style={{ marginBottom: 12 }}>
            <Segmented label="Kind" idPrefix="kind" value={filter} onChange={setFilter} options={[{ id: 'all', label: 'All' }, { id: 'earning', label: 'Earnings' }, { id: 'deduction', label: 'Deductions' }]} />
            <Switch checked={showInactive} onChange={setShowInactive} label="Show inactive" showLabel />
          </div>
          {q.isLoading ? (
            <Skeleton h={420} />
          ) : (
            <ul className="list-plain panel" style={{ padding: '4px 0' }}>
              {list.map((c) => {
                const rule = c.rules.find((r) => !r.officeId);
                const active = c.id === current?.id;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(c.id)}
                      aria-pressed={active}
                      className="row"
                      style={{ width: '100%', padding: '12px 18px', border: 0, background: active ? 'var(--bg-3)' : 'transparent', textAlign: 'left', cursor: 'pointer', borderLeft: `2px solid ${active ? 'var(--accent)' : 'transparent'}`, transition: 'background var(--dur-2)' }}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: c.kind === 'earning' ? 'var(--sage)' : 'var(--clay)', flex: 'none' }} />
                      <span style={{ minWidth: 0, opacity: c.archivedAt ? 0.55 : 1 }}>
                        <span style={{ display: 'block' }}>
                          {c.name} {c.archivedAt ? <Chip>Inactive</Chip> : null}
                        </span>
                        <span className="mono faint" style={{ fontSize: 10.5 }}>
                          {c.code}
                          {c.role ? ` · ${c.role}` : ''}
                        </span>
                      </span>
                      <span style={{ marginLeft: 'auto', whiteSpace: 'nowrap', paddingLeft: 12 }} className="mono faint">
                        {!rule ? 'per person' : rule.useFormula ? 'ƒ formula' : idr(rule.amount)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="col-7" style={{ position: 'sticky', top: 80 }}>
          <AnimatePresence mode="wait">
            {current ? (
              <motion.div key={current.id} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} transition={{ duration: 0.35, ease: EASE }}>
                <RuleEditor component={current} reference={ref.data} canEdit={canEdit && !current.archivedAt} onDetails={canEdit ? () => setEditing(current) : undefined} />
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </div>
      {editing ? <ComponentDialog component={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onCreated={setSelected} /> : null}
    </div>
  );
}

/** "Basic Salary Bonus" → "BASIC_SALARY_BONUS", as the code field allows. */
const codeFrom = (name: string) => name.toUpperCase().normalize('NFKD').replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 24);

/** A new component, or an existing one's name, notes, flags and whether it is in use. */
function ComponentDialog({ component: c, onClose, onCreated }: { component: Component | null; onClose: () => void; onCreated: (id: string) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(c?.name ?? '');
  const [code, setCode] = useState(c?.code ?? '');
  const [codeTouched, setCodeTouched] = useState(false);
  const [kind, setKind] = useState<'earning' | 'deduction'>(c?.kind ?? 'earning');
  const [description, setDescription] = useState(c?.description ?? '');
  const [excludedFromThr, setExcludedFromThr] = useState(c?.excludedFromThr ?? false);
  const [manual, setManual] = useState(c?.manual ?? false);
  const done = (title: string) => {
    toast({ title });
    void qc.invalidateQueries({ queryKey: ['components'] });
    onClose();
  };
  const save = useMutation({
    mutationFn: () =>
      c
        ? patch<Component>(`/api/payroll/components/${c.id}`, { name: name.trim(), description: description.trim() || null, excludedFromThr, manual })
        : post<Component>('/api/payroll/components', { code: (codeTouched ? code : codeFrom(name)).trim(), name: name.trim(), kind, description: description.trim() || undefined, excludedFromThr, manual }),
    onSuccess: (row) => {
      if (!c) onCreated(row.id);
      done(c ? `${name.trim()} saved` : `${name.trim()} added`);
    },
  });
  const toggle = useMutation({ mutationFn: () => patch(`/api/payroll/components/${c!.id}`, { archived: !c!.archivedAt }), onSuccess: () => done(c!.archivedAt ? `${c!.name} is active again` : `${c!.name} deactivated`) });
  const error = save.error ?? toggle.error;
  const shownCode = c ? c.code : codeTouched ? code : codeFrom(name);
  return (
    <Dialog
      open
      onClose={onClose}
      eyebrow={c ? (c.kind === 'earning' ? 'Earning' : 'Deduction') : 'New component'}
      title={c ? c.name : 'Add a grant or deduction'}
      width={560}
      footer={
        <>
          {c && !c.role ? (
            <Button variant="ghost" loading={toggle.isPending} onClick={() => toggle.mutate()}>
              {c.archivedAt ? 'Reactivate' : 'Deactivate'}
            </Button>
          ) : null}
          <span style={{ flex: 1 }} />
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={name.trim().length < 2 || (!c && !/^[A-Z0-9_]{2,24}$/.test(shownCode))} onClick={() => save.mutate()}>
            {c ? 'Save' : 'Add'}
          </Button>
        </>
      }
    >
      <div className="grid-12" style={{ gap: 14 }}>
        <div className="col-8">
          <Field label="Name">{(p) => <input {...p} className="input" value={name} placeholder="Transport allowance" onChange={(e) => setName(e.target.value)} />}</Field>
        </div>
        <div className="col-4">
          <Field label="Code" hint={c ? 'Fixed once created' : 'Capitals, digits, _'}>
            {(p) => <input {...p} className="input input--mono" value={shownCode} disabled={!!c} onChange={(e) => { setCodeTouched(true); setCode(e.target.value.toUpperCase()); }} />}
          </Field>
        </div>
        {!c ? (
          <div className="col-12">
            <Segmented label="Kind" idPrefix="new-kind" value={kind} onChange={setKind} options={[{ id: 'earning', label: 'Earning (grant)' }, { id: 'deduction', label: 'Deduction' }]} />
          </div>
        ) : null}
        <div className="col-12">
          <Field label="Notes" hint="Optional. What it is for, who gets it.">{(p) => <textarea {...p} className="textarea" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />}</Field>
        </div>
        <div className="col-12 stack" style={{ ['--gap' as string]: '10px' }}>
          <Switch checked={excludedFromThr} onChange={setExcludedFromThr} label="Leave out of the holiday allowance (THR)" showLabel />
          <Switch checked={manual} onChange={setManual} label="Entered by hand each period" showLabel />
        </div>
        {!c ? (
          <div className="col-12">
            <Callout tone="info">Next, give it a company-wide amount or formula on this page, or add it to the people who get it on their Pay &amp; bank tab.</Callout>
          </div>
        ) : c.role ? (
          <div className="col-12">
            <Callout tone="info">Payroll uses this component for {c.role.replace(/_/g, ' ')}, so it stays active.</Callout>
          </div>
        ) : null}
        {error ? (
          <div className="col-12">
            <Callout tone="bad">{error instanceof ApiError ? error.message : 'Something went wrong.'}</Callout>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

function RuleEditor({ component: c, reference, canEdit, onDetails }: { component: Component; reference?: Reference; canEdit: boolean; onDetails?: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const rule = c.rules.find((r) => !r.officeId);
  const [mode, setMode] = useState<'amount' | 'formula'>(rule?.useFormula ? 'formula' : 'amount');
  const [amount, setAmount] = useState(rule?.amount ?? '');
  const [formula, setFormula] = useState(rule?.formula ?? '');
  const [active, setActive] = useState(rule?.active ?? true);
  const [check, setCheck] = useState<{ ok: boolean; variables: string[]; error?: { message: string; position: number }; preview: string | null; previewError: string | null } | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (mode !== 'formula' || !formula.trim()) return setCheck(null);
    const t = setTimeout(async () => {
      try {
        setCheck(await post('/api/payroll/formula/check', { source: formula, sample: SAMPLE }));
      } catch {
        setCheck(null);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [formula, mode]);

  const save = useMutation({
    mutationFn: () => put(`/api/payroll/components/${c.id}/rule`, { amount: mode === 'amount' ? amount || '0' : null, useFormula: mode === 'formula', formula: mode === 'formula' ? formula : null, active }),
    onSuccess: () => {
      toast({ title: 'Rule saved', body: 'Applies from the next calculation.' });
      void qc.invalidateQueries({ queryKey: ['components'] });
    },
    onError: (e) => toast({ title: 'Not saved', body: e instanceof ApiError ? e.message : undefined, tone: 'error' }),
  });

  const insert = (name: string) => {
    const el = area.current;
    const token = `_${name}_`;
    if (!el) return setFormula((f) => f + token);
    const { selectionStart: a, selectionEnd: b } = el;
    const next = formula.slice(0, a) + token + formula.slice(b);
    setFormula(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(a + token.length, a + token.length);
    });
  };

  const usedNotes = useMemo(() => (reference?.variables ?? []).filter((v) => check?.variables.includes(v.name) && v.legacyNote), [reference, check]);
  const dedicated = ['overtime_grant', 'bpjs_fee', 'income_tax', 'loan_payment', 'mandatory_saving', 'voluntary_saving', 'performance_grant_15th', 'holiday_allowance'].includes(c.role ?? '');

  return (
    <Panel
      eyebrow={c.kind === 'earning' ? 'Earning' : 'Deduction'}
      title={c.name}
      action={
        <div className="row" style={{ ['--gap' as string]: '10px' }}>
          {onDetails ? (
            <Button size="sm" variant="ghost" onClick={onDetails}>
              Details
            </Button>
          ) : null}
          <span className="faint" style={{ fontSize: 'var(--step--2)' }}>
            Active
          </span>
          <Switch checked={active} onChange={setActive} label="Rule active" disabled={!canEdit} />
        </div>
      }
    >
      <div className="stack">
        <div className="row row--wrap" style={{ ['--gap' as string]: '6px' }}>
          <Chip tone="plain">{c.code}</Chip>
          {c.role ? <Chip tone="accent">{c.role.replace(/_/g, ' ')}</Chip> : null}
          {c.cooperative ? <Chip tone="info">cooperative</Chip> : null}
          {c.salaryProrate ? <Chip tone="info">prorate</Chip> : null}
          {c.excludedFromThr ? <Chip tone="plain">not in THR</Chip> : null}
          {c.splitPayrollCut ? <Chip tone="plain">split cut</Chip> : null}
          {c.manual ? <Chip tone="plain">entered by hand</Chip> : null}
          {c.archivedAt ? <Chip tone="warn">inactive</Chip> : null}
        </div>
        {c.description ? <p className="muted" style={{ fontSize: 'var(--step--1)' }}>{c.description}</p> : null}
        {dedicated ? (
          <Callout tone="info" title="Dedicated stage.">
            The engine applies this component at a fixed point in the calculation{c.role === 'bpjs_fee' || c.role === 'loan_payment' ? ', and only if net pay can cover it' : ''}. Per-employee amounts are set on each person.
          </Callout>
        ) : null}
        <Segmented label="Value type" idPrefix={`mode-${c.id}`} value={mode} onChange={setMode} options={[{ id: 'amount', label: 'Fixed amount' }, { id: 'formula', label: 'Formula' }]} />
        {mode === 'amount' ? (
          <Field label="Amount per period" hint="Rupiah, for everyone. An amount on a person's Pay & bank tab is paid on top of this.">
            {(p) => <input {...p} className="input input--mono" inputMode="decimal" value={amount ?? ''} onChange={(e) => setAmount(e.target.value)} disabled={!canEdit} />}
          </Field>
        ) : (
          <div className="stack" style={{ ['--gap' as string]: '12px' }}>
            <div className="formula-editor">
              <Field label="Formula" error={check && !check.ok ? `${check.error?.message} (at character ${(check.error?.position ?? 0) + 1})` : null}>
                {(p) => <textarea {...p} ref={area} className="textarea" spellCheck={false} value={formula} onChange={(e) => setFormula(e.target.value)} disabled={!canEdit} />}
              </Field>
            </div>
            <div className="row row--wrap" style={{ ['--gap' as string]: '6px' }}>
              {(reference?.variables ?? []).map((v) => (
                <button key={v.name} type="button" className="var-chip" title={`${v.label}: ${v.description}`} onClick={() => insert(v.name)} disabled={!canEdit}>
                  {v.name}
                </button>
              ))}
            </div>
            <div className="row row--between" style={{ padding: '14px 16px', borderRadius: 'var(--radius-sm)', background: 'var(--bg-1)', border: '1px solid var(--line-1)' }}>
              <span className="faint" style={{ fontSize: 'var(--step--1)' }}>
                With sample values (basic Rp 5 jt, 37 min late, 1 day absent, 2 h overtime)
              </span>
              <span className="display num" style={{ fontSize: '1.6rem', color: check?.ok ? 'var(--accent-text)' : 'var(--ink-4)' }}>
                {check?.ok ? (check.preview ? idr(check.preview, { decimals: true }) : check.previewError ?? '—') : '—'}
              </span>
            </div>
            {usedNotes.map((v) => (
              <Callout key={v.name} title={`${v.name}:`}>
                {v.legacyNote}
              </Callout>
            ))}
          </div>
        )}
        {canEdit ? (
          <div className="row">
            <Button variant="primary" icon="check" loading={save.isPending} disabled={mode === 'formula' && !check?.ok} onClick={() => save.mutate()}>
              Save rule
            </Button>
            <span className="faint" style={{ fontSize: 'var(--step--2)' }}>
              <Icon name="scroll" width={13} height={13} style={{ display: 'inline', verticalAlign: '-2px' }} /> Every change is recorded in the audit trail.
            </span>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
